import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api/client.js';
import type { ActivityEntry, ActivityQuery, ActivityScope } from '../../lib/api/types.js';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { Select } from '../../ui/primitives/Select.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DataTable, type DataTableColumn } from '../../ui/components/DataTable/DataTable.tsx';
import { Drawer, DrawerSection } from '../../ui/components/Drawer/Drawer.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { Pagination } from '../../ui/components/Pagination/Pagination.tsx';
import { Toolbar } from '../../ui/components/Toolbar/Toolbar.tsx';
import styles from './admin.module.css';

/**
 * WHO DID WHAT ON THE PLATFORM — the activity log, newest first
 * (`GET /api/admin/activity`; FR-083).
 *
 * Every write the API served is here, from administrators and learners alike,
 * with sign-ins, sign-outs and the operator's own commands. It is READ-ONLY:
 * the trail an administrator's acts leave cannot be edited or deleted, from
 * this screen or from anywhere in the application.
 *
 * The table answers "what happened"; a row's Detalhes answers "exactly what" —
 * the body that was sent, a role's before and after, and, for a deleted
 * Lesson or Exercise, the whole of what was deleted, which is the way to
 * rebuild it by hand.
 */

const PAGE_SIZES = [20, 50, 100] as const;

const SCOPE_LABEL: Record<ActivityScope, string> = {
  admin: 'Administração',
  learner: 'Aluno',
  auth: 'Acesso',
  operator: 'Operador',
};

/**
 * The dotted names the API records, as the screen says them. An action not
 * listed here — a route added later that nobody named — shows its raw name,
 * which is still readable and is never hidden.
 */
const ACTION_LABEL: Record<string, string> = {
  'lesson.create': 'Criou uma lição',
  'lesson.update': 'Editou uma lição',
  'lesson.reorder': 'Reordenou as lições',
  'lesson.delete': 'Excluiu uma lição',
  'exercise.create': 'Criou um exercício',
  'exercise.update': 'Editou um exercício',
  'exercise.reorder': 'Reordenou os exercícios',
  'exercise.delete': 'Excluiu um exercício',
  'media.upload': 'Enviou um áudio',
  'user.role': 'Alterou uma função',
  'user.unfreeze': 'Liberou um administrador',
  'admin.frozen': 'Foi congelado pelo limite',
  'admin.blocked': 'Tentou alterar estando congelado',
  'admin.denied': 'Tentou abrir a administração sem acesso',
  'platform.lockdown': 'Bloqueio geral',
  'auth.sign_up': 'Criou a conta',
  'auth.sign_in': 'Entrou',
  'auth.sign_out': 'Saiu',
  'account.update': 'Alterou a própria conta',
  'account.delete': 'Excluiu a própria conta',
  'account.erase_data': 'Apagou o próprio progresso',
  'attempt.submit': 'Respondeu um exercício',
  'lesson.restart': 'Recomeçou uma lição',
  'learning.next': 'Avançou na trilha',
  'learning.session_start': 'Começou a praticar',
  'follow.add': 'Seguiu alguém',
  'follow.remove': 'Deixou de seguir alguém',
  'block.add': 'Bloqueou alguém',
  'block.remove': 'Desbloqueou alguém',
  'subscription.start': 'Assinou um plano',
  'subscription.cancel': 'Cancelou a assinatura',
  'subscription.reactivate': 'Reativou a assinatura',
};

const TARGET_LABEL: Record<string, string> = {
  user: 'Conta',
  lesson: 'Lição',
  exercise: 'Exercício',
  attempt: 'Resposta',
};

function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

/*
 * Date AND time, to the second: a log is read for sequence, and two acts a
 * few seconds apart are often the whole story.
 */
function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Who acted, as plainly as the row allows. */
function actorLabel(entry: ActivityEntry): string {
  if (entry.scope === 'operator') return 'Operador';
  if (entry.actorName) return entry.actorName;
  if (entry.actorEmail) return `${entry.actorEmail} (conta excluída)`;
  return 'Conta excluída';
}

/** The address a role change or a freeze recorded for the account it was about. */
function targetEmailOf(detail: unknown): string | null {
  const email = (detail as { targetEmail?: unknown } | null)?.targetEmail;
  return typeof email === 'string' ? email : null;
}

function Outcome({ status }: { status: number | null }) {
  if (status === null) return <Badge tone="neutral">—</Badge>;
  if (status < 400) return <Badge tone="success">{status}</Badge>;
  return <Badge tone="near-miss">{status}</Badge>;
}

interface ActivityData {
  entries: ActivityEntry[];
  total: number;
}

const EMPTY: ActivityData = { entries: [], total: 0 };

export function ActivityList() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[1]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<ActivityScope | 'all'>('all');
  const [failedOnly, setFailedOnly] = useState(false);
  const [open, setOpen] = useState<ActivityEntry | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [query, scope, failedOnly, pageSize]);

  const request: ActivityQuery = useMemo(
    () => ({ page, pageSize, search: query, scope, failedOnly }),
    [page, pageSize, query, scope, failedOnly],
  );

  const load = useCallback(async (): Promise<ActivityData> => {
    const response = await api.adminActivity(request);
    return { entries: response.entries, total: response.total };
  }, [request]);

  const {
    status,
    data: { entries, total },
    showLoading,
    refreshing: busy,
    reload,
  } = useAsyncData(load, EMPTY, [request]);

  const columns: DataTableColumn<ActivityEntry>[] = useMemo(
    () => [
      {
        id: 'occurredAt',
        header: 'Quando',
        width: 'min',
        cell: (entry) => (
          <span className="type-numeric-inline">{formatMoment(entry.occurredAt)}</span>
        ),
      },
      {
        id: 'actor',
        header: 'Quem',
        rowHeader: true,
        cell: (entry) => actorLabel(entry),
      },
      {
        id: 'action',
        header: 'O que',
        cell: (entry) => actionLabel(entry.action),
      },
      {
        id: 'status',
        header: 'Resultado',
        width: 'min',
        align: 'end',
        cell: (entry) => <Outcome status={entry.status} />,
      },
      {
        id: 'details',
        header: 'Detalhes',
        width: 'min',
        align: 'end',
        cell: (entry) => (
          <div className={styles.rowActions}>
            <Button
              variant="ghost"
              size="compact"
              ariaLabel={`Detalhes: ${actionLabel(entry.action)}, ${formatMoment(entry.occurredAt)}`}
              onClick={() => setOpen(entry)}
            >
              Detalhes
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  if (status === 'loading') {
    return showLoading ? <LoadingState label="Carregando a atividade" /> : null;
  }

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <Text as="h1" variant="subheading">
          Não foi possível carregar a atividade
        </Text>
        <span>
          <Button onClick={() => reload({ force: true })}>Tentar novamente</Button>
        </span>
      </div>
    );
  }

  const filtered = query !== '' || scope !== 'all' || failedOnly;

  return (
    <div className={styles.page} data-testid="admin-activity">
      <header className={styles.header}>
        <div className={styles.heading}>
          <Text as="h1" variant="subheading">
            Atividade
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            Tudo o que foi alterado na plataforma, por quem e quando. O registro dos administradores
            é permanente e não pode ser apagado.
          </Text>
        </div>
      </header>

      <section className={styles.section}>
        <Toolbar label="Filtrar atividade">
          <Input
            id="activity-search"
            type="search"
            size="compact"
            icon="search"
            ariaLabel="Buscar por nome, e-mail ou ação"
            placeholder="Buscar por nome, e-mail ou ação"
            value={search}
            onChange={setSearch}
          />
          <Select
            id="activity-scope"
            size="compact"
            ariaLabel="Filtrar por origem"
            value={scope}
            options={[
              { value: 'all', label: 'Todas as origens' },
              { value: 'admin', label: SCOPE_LABEL.admin },
              { value: 'operator', label: SCOPE_LABEL.operator },
              { value: 'auth', label: SCOPE_LABEL.auth },
              { value: 'learner', label: SCOPE_LABEL.learner },
            ]}
            onChange={(next) => setScope(next as ActivityScope | 'all')}
          />
          <Select
            id="activity-outcome"
            size="compact"
            ariaLabel="Filtrar por resultado"
            value={failedOnly ? 'failed' : 'all'}
            options={[
              { value: 'all', label: 'Todos os resultados' },
              { value: 'failed', label: 'Só recusas e erros' },
            ]}
            onChange={(next) => setFailedOnly(next === 'failed')}
          />
          {filtered ? (
            <Button
              variant="ghost"
              size="compact"
              onClick={() => {
                setSearch('');
                setScope('all');
                setFailedOnly(false);
              }}
            >
              Limpar
            </Button>
          ) : null}
        </Toolbar>

        <DataTable
          caption="Atividade da plataforma"
          testId="activity"
          rowTestId="activity-row"
          columns={columns}
          rows={entries}
          rowKey={(entry) => entry.id}
          busy={busy}
          empty={
            <Text as="p" variant="body-sm" tone="muted">
              {filtered ? 'Nada corresponde a esses filtros.' : 'Nenhuma atividade ainda.'}
            </Text>
          }
        />

        <Pagination
          label="Páginas de atividade"
          itemLabel="registros"
          page={page}
          pageCount={Math.max(1, Math.ceil(total / pageSize))}
          total={total}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </section>

      <Drawer
        open={open !== null}
        title={open ? actionLabel(open.action) : ''}
        description={open ? formatMoment(open.occurredAt) : undefined}
        onClose={() => setOpen(null)}
      >
        {open ? (
          <>
            <DrawerSection title="Registro">
              <dl className={styles.detailList}>
                <dt>Quem</dt>
                <dd>
                  {actorLabel(open)}
                  {open.actorEmail && open.actorName ? ` · ${open.actorEmail}` : ''}
                </dd>
                <dt>Origem</dt>
                <dd>{SCOPE_LABEL[open.scope]}</dd>
                <dt>Ação</dt>
                <dd>{open.action}</dd>
                {open.targetType ? (
                  <>
                    <dt>Alvo</dt>
                    <dd>
                      {TARGET_LABEL[open.targetType] ?? open.targetType}
                      {targetEmailOf(open.detail) ? ` · ${targetEmailOf(open.detail)}` : ''}
                      {open.targetId ? ` · ${open.targetId}` : ''}
                    </dd>
                  </>
                ) : null}
                <dt>Resultado</dt>
                <dd>{open.status ?? '—'}</dd>
                <dt>Endereço</dt>
                <dd>{open.ip ?? '—'}</dd>
                <dt>Navegador</dt>
                <dd>{open.userAgent ?? '—'}</dd>
              </dl>
            </DrawerSection>
            {open.detail !== null && open.detail !== undefined ? (
              <DrawerSection
                title="Conteúdo"
                description="O que foi enviado e, numa exclusão, tudo o que foi excluído."
              >
                <pre className={styles.detailCode}>{JSON.stringify(open.detail, null, 2)}</pre>
              </DrawerSection>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
